import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Database, 
  FileSpreadsheet, 
  BarChart3, 
  PieChart, 
  Code,
  Award,
  Calendar,
  MapPin,
  GraduationCap,
  CheckCircle
} from "lucide-react";

const About = () => {
  const skills = [
    { name: "SQL", level: 80, icon: Database },
    { name: "Python", level: 85, icon: Code },
    { name: "Tableau", level: 80, icon: PieChart },
    { name: "Excel", level: 80, icon: FileSpreadsheet },
    { name: "Power BI", level: 75, icon: BarChart3 },
    { name: "R", level: 34, icon: Code },
  ];

  const certifications = [
    {
      title: "Google Data Analytics Professional Certificate",
      issuer: "Coursea",
      date: "2024",
      status: "Completed"
    },
    {
      title: "Career Essentials in Data Analysis by Microsoft & LinkedIn",
      issuer: "LinkedIn",
      date: "2024",
      status: "Completed"
    },
    {
      title: "Database Management System - Science Graduates",
      issuer: "Infosys Springboard",
      date: "2024",
      status: "Completed"
    },
    {
      title: "Accenture North America's Data Analytics and Visualization",
      issuer: "Forage",
      date: "2024",
      status: "Completed"
    }
  ];

  const timeline = [
    {
      year: "2025",
      title: "Infosys Springboard Internship 6.0 (Data Visualization Domain)",
      description: "Working on various data analytics projects"
    },
    {
      year: "2025",
      title: "Masters's degree",
      description: "Graduated with degree in MBA (Finace And Marketing)"
    },
    {
      year: "2024",
      title: "Google Data Analytics Certificate",
      description: "Completed comprehensive data analytics program"
    },
    {
      year: "2024",
      title: "Started Data Analytics Journey",
      description: "Began learning SQL, Python, and data visualization tools"
    },
    {
      year: "2023",
      title: "Bachelor's Degree",
      description: "Graduated with degree in B.Com (Computer Applications)"
    }
  ];

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12" data-aos="fade-up">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            About Me
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            I'm a passionate data analyst who loves turning complex data into 
            clear, actionable insights that drive business decisions.
          </p>
        </div>

        {/* Personal Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <Card className="glassmorphism" data-aos="fade-right">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  My Story
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  At 23 years old, I'm a data analyst from Indore, India, 
                  passionate about transforming raw data into meaningful insights. 
                  My journey into analytics began during my MBA studies, 
                  where I discovered how data-driven decision-making can shape business strategy and growth.
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  With a foundation in B.Com and MBA, I bring a strong business perspective along with technical skills in 
                  SQL, Python, Tableau, Excel, and Power BI. I recently completed the Google Data Analytics Professional Certificate and 
                  the Career Essentials in Data Analysis by Microsoft & LinkedIn, which strengthened my skills in 
                  data cleaning, visualization, and analytics.
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  I have applied these skills in projects like the Cyclistic Bike-Share Case Study and 
                  a Layoffs Analysis Project. Currently, I am pursuing my Infosys Springboard Internship (Data Visualization Domain), 
                  where I am gaining practical exposure in dataset cleaning and dashboard building.
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  My interests lie in business intelligence, predictive analytics, and data storytelling, 
                  and I am committed to continuous learning to stay ahead in the data field.
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card className="glassmorphism-card" data-aos="fade-left">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Calendar className="h-4 w-4" />
                    <span>Age: 23</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <MapPin className="h-4 w-4" />
                    <span>Indore, Madhya Pradesh, India</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Award className="h-4 w-4" />
                    <span>Google Certified</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <CheckCircle className="h-4 w-4" />
                    <span>Available for opportunities</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Skills Section */}
<div className="mb-16">
  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center" data-aos="fade-up">
    Technical Skills
  </h2>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {skills.map((skill, index) => (
      <Card key={skill.name} className="glassmorphism-card" data-aos="fade-up" data-aos-delay={index * 100}>
        <CardContent className="p-6">
          {/* Skill Name */}
          <div className="flex items-center gap-3 mb-4">
            <skill.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold text-gray-900 dark:text-white">
              {skill.name}
            </span>
          </div>

          {/* Skill Levels */}
          <div className="flex gap-2">
            {/* Beginner */}
            <div
              className={`h-6 flex-1 flex items-center justify-center rounded-md text-xs font-medium ${
                skill.level >= 33
                  ? "bg-blue-600 text-white"
                  : "bg-gray-300 text-gray-600"
              }`}
            >
              Beginner
            </div>
            {/* Intermediate */}
            <div
              className={`h-6 flex-1 flex items-center justify-center rounded-md text-xs font-medium ${
                skill.level >= 66
                  ? "bg-blue-600 text-white"
                  : "bg-gray-300 text-gray-600"
              }`}
            >
              Intermediate
            </div>
            {/* Expert */}
            <div
              className={`h-6 flex-1 flex items-center justify-center rounded-md text-xs font-medium ${
                skill.level >= 90
                  ? "bg-blue-600 text-white"
                  : "bg-gray-300 text-gray-600"
              }`}
            >
              Expert
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
</div>


        {/* Certifications */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center" data-aos="fade-up">
            Certifications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="glassmorphism-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant={cert.status === "Completed" ? "default" : "secondary"}>
                      {cert.status}
                    </Badge>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {cert.date}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {cert.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {cert.issuer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center" data-aos="fade-up">
            Professional Timeline
          </h2>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-600 dark:bg-blue-400"></div>
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="relative flex items-start" data-aos="fade-right" data-aos-delay={index * 100}>
                  <div className="absolute left-0 w-8 h-8 bg-blue-600 dark:bg-blue-400 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <div className="ml-12">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {item.year}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Interests */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center" data-aos="fade-up">
            Interests & Hobbies
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="glassmorphism-card" data-aos="zoom-in" data-aos-delay="100">
              <CardContent className="p-6 text-center">
                <BarChart3 className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Data Visualization
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Creating beautiful and insightful visualizations
                </p>
              </CardContent>
            </Card>
            <Card className="glassmorphism-card" data-aos="zoom-in" data-aos-delay="200">
              <CardContent className="p-6 text-center">
                <Code className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Open Source
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Contributing to data science projects
                </p>
              </CardContent>
            </Card>
            <Card className="glassmorphism-card" data-aos="zoom-in" data-aos-delay="300">
              <CardContent className="p-6 text-center">
                <GraduationCap className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Continuous Learning
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Staying updated with latest trends
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;